// @ts-nocheck
import { unique } from "remeda";
import type { GridApi } from "ag-grid-community";
import {
  useActivePageId,
  useDeleteConditionalFormattingRules,
  useQueryConditionalFormattingRulesGrouped,
  useUpdatePageFormatting,
  usePageFormattingOptions,
  usePageProducts,
  useUpdatePageProducts,
} from "../../../../data";
import { monthCodeToShortMonthAndYear, rowIdToRelativeRow } from "@package/grid-utils";
import { PaletteIcon } from "../../../icons/PaletteIcon";
import { ClearFormattingIcon } from "../../../icons/ClearFormattingIcon";
import { getCellIds } from "../../../market-grid/cell-style/helpers";
import type { MenuConfig } from "../../types";
import type { ActionContext } from "../../../actions/types";
import type { TRangeSelection } from "../../../market-grid/useRangeSelectionChange";
import { selection } from "../../disabled-helpers";
import { defaultColumnSettings } from "../../../market-grid/columns/columnDefs";

type TClearCellConditionalFormatting = {
  type: "range" | "column" | "period" | "all";
  periods?: string[];
  cellIds?: string[];
  colIds?: string[];
};

const formatTypes = [
  { item: "range", type: "range" },
  { item: "column", type: "column" },
  { item: "period", type: "period" },
] satisfies Array<{
  item: "range" | "column" | "period";
  type: "range" | "column" | "period";
}>;

function getFormatLabel({
  api,
  selection,
  item,
  isHeaderMenu,
}: {
  api: GridApi;
  selection: TRangeSelection;
  item: "range" | "column" | "period";
  isHeaderMenu?: boolean;
}): string {
  try {
    if (!api) return "";

    if (isHeaderMenu) {
      switch (item) {
        case "range":
          return "Range";
        case "column":
          return "Column";
        case "period":
          return "Period";
      }
    }

    const sortedRangeSelection = selection?.flat()?.sort((a, b) => {
      return a?.rowIndex ?? 0 - (b?.rowIndex ?? 0);
    });

    if (!sortedRangeSelection?.length) return "";

    const firstCell = sortedRangeSelection[0];
    const lastCell = sortedRangeSelection[sortedRangeSelection.length - 1];

    const firstPeriod = firstCell?.rowId;
    const lastPeriod = lastCell?.rowId;

    switch (item) {
      case "range":
        return "Range";
      case "column":
        return unique(sortedRangeSelection.map((cell) => cell.columnId)).length > 1 ? "Columns" : "Column";
      case "period":
        return firstPeriod === lastPeriod ? `Period: ${firstPeriod}` : `Periods: ${firstPeriod} - ${lastPeriod}`;
    }
  } catch (e) {
    console.error("Error getting format label", e);
    return "";
  }
}

function getClearFormattingParams(
  type: "range" | "column" | "period",
  ctx: ActionContext,
): TClearCellConditionalFormatting {
  const { selectedRange: ranges, api } = ctx;
  switch (type) {
    case "range":
      return { type, cellIds: getCellIds({ type: "range", ranges, api }).cellIds };
    case "column":
      return { type, colIds: getCellIds({ type: "column", ranges, api }).cellIds };
    case "period":
      return { type, periods: getCellIds({ type: "period", ranges, api }).cellIds };
  }
}

const useClearConditionalFormatting = () => {
  const pageId = useActivePageId();
  const rules = useQueryConditionalFormattingRulesGrouped(pageId);
  const remove = useDeleteConditionalFormattingRules(pageId);

  return ({ type, periods, cellIds, colIds }: TClearCellConditionalFormatting) => {
    function clearCell() {
      const toRemove = cellIds?.flatMap((cellId) => rules.data?.[cellId]?.map((r) => r.id))?.filter(Boolean) || [];
      remove.mutateAsync(toRemove);
    }

    function clearColumn() {
      const ids =
        colIds
          ?.flatMap((colId) => {
            return Object.values(rules.data || {}).flatMap((rules) => {
              return rules?.map((r) => {
                if (r.column_id === colId) {
                  return r.id;
                }
              });
            });
          })
          ?.filter(Boolean) || [];
      remove.mutate(ids);
    }

    function clearPeriod() {
      const ids =
        periods
          ?.flatMap((code) => {
            const period = monthCodeToShortMonthAndYear(code);
            const rowId = rowIdToRelativeRow?.[period];

            return Object.values(rules.data || {}).flatMap((rules) => {
              return rules?.map((r) => {
                if (r.row_id === rowId) {
                  return r.id;
                }
              });
            });
          })
          ?.filter(Boolean) || [];
      remove.mutate(ids);
    }

    if (type === "range") clearCell();
    if (type === "column") clearColumn();
    if (type === "period") clearPeriod();
    if (type === "all") {
      clearCell();
      clearColumn();
      clearPeriod();
    }
  };
};

export const useClearFormatting = () => {
  const clearCellConditionalFormatting = useClearConditionalFormatting();
  const update = useUpdatePageFormatting();
  const pageId = useActivePageId();
  const formatting = usePageFormattingOptions(pageId);

  const pageProducts = usePageProducts(pageId);
  const updatePageProducts = useUpdatePageProducts();

  return (params: TClearCellConditionalFormatting) => {
    clearCellConditionalFormatting(params);
    const current = formatting.data;

    if (params.type === "range") {
      const cell_highlights = { ...current?.cell_highlights };
      params.cellIds?.forEach((cellId) => delete cell_highlights[cellId]);
      return update({ id: pageId, cell_highlights });
    }

    if (params.type === "column") {
      const column_highlights = { ...current?.column_highlights };
      params.colIds?.forEach((colId) => delete column_highlights[colId]);

      // Also clear decimalPlaces, thousandsSeparator, and increment.
      const updatedPageProducts = (pageProducts.data ?? [])
        .filter((p) => params.colIds?.includes(p.id))
        .map((p) => ({
          ...p,
          decimal_places: null,
          thousands_separator: defaultColumnSettings.thousandsSeparator,
        }));

      return Promise.all([
        update({ id: pageId, column_highlights }),
        ...updatedPageProducts.map(updatePageProducts.optimistic),
      ]);
    }

    if (params.type === "period") {
      const period_highlights = { ...current?.period_highlights };
      params.periods?.forEach((period) => delete period_highlights[period]);
      return update({ id: pageId, period_highlights });
    }

    if (params.type === "all") {
      return update({
        id: pageId,
        cell_highlights: {},
        column_highlights: {},
        period_highlights: {},
      });
    }
  };
};

export const clearFormattingSection: MenuConfig[] = [
  {
    id: "clear-formatting",
    label: "Clear formatting",
    icon: <ClearFormattingIcon />,
    action: () => {},
    subMenu: (ctx) => {
      const { selectedRange: ranges, api, isHeaderMenu } = ctx;

      return formatTypes.map(({ item, type }) => ({
        id: `clear-${type}-formatting`,
        label: getFormatLabel({ api, selection: ranges, item, isHeaderMenu }),
        disabled: (ctx: ActionContext) => (type === "range" || type === "period") && selection.isFullColumn(ctx),
        action: (ctx: ActionContext) => () => {
          ctx.clearFormatting(getClearFormattingParams(type, ctx));
        },
      }));
    },
  },
];

export const formatSection: MenuConfig[] = [
  {
    id: "format",
    label: "Format",
    icon: <PaletteIcon />,
    action: () => {},
    subMenu: (ctx) => {
      const { selectedRange: ranges, api, pageId, columnSettingsModal, isHeaderMenu } = ctx;

      return [
        ...formatTypes.map(({ item, type }) => ({
          id: `format-${type}`,
          label: getFormatLabel({ api, selection: ranges, item, isHeaderMenu }),
          disabled: (ctx: ActionContext) => (type === "range" || type === "period") && selection.isFullColumn(ctx),
          action: () => {
            columnSettingsModal.open({
              active: "color",
              selection: ranges,
              pageId,
              type,
              width: 430,
              height: 430,
            });
          },
        })),
        { ...clearFormattingSection[0], icon: undefined },
      ];
    },
  },
];
